export type VideoAspectRatio = '1:1' | '9:16' | '16:9';

export type VideoOverlayType = 'text' | 'cta' | 'logo';

export type VideoAnimation =
  | 'none'
  | 'fade-in'
  | 'slide-up'
  | 'slide-left'
  | 'pop-in';

export interface VideoOverlay {
  id: string;
  type: VideoOverlayType;
  content: string;
  x: number; // 0-100 percentage
  y: number; // 0-100 percentage
  width: number; // 0-100 percentage
  fontSize: number;
  fontFamily: string;
  color: string;
  backgroundColor: string;
  borderRadius: number;
  fontWeight: number;
  opacity: number;
  animation: VideoAnimation;
  startTime: number; // seconds
  duration: number; // seconds
  // Logo/image specific
  imageUrl?: string;
  // CTA specific
  icon?: string;
  // Text preset
  preset?: 'headline' | 'subheadline' | 'cta-text';
}

export interface VideoEditorState {
  videoId: string;
  videoUrl: string;
  aspectRatio: VideoAspectRatio;
  overlays: VideoOverlay[];
}

export const FONT_OPTIONS = [
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Montserrat', value: 'Montserrat, sans-serif' },
  { label: 'Poppins', value: 'Poppins, sans-serif' },
  { label: 'Oswald', value: 'Oswald, sans-serif' },
  { label: 'Raleway', value: 'Raleway, sans-serif' },
  { label: 'Bebas Neue', value: '"Bebas Neue", sans-serif' },
  { label: 'Playfair Display', value: '"Playfair Display", serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Impact', value: 'Impact, sans-serif' },
  { label: 'Arial Black', value: '"Arial Black", sans-serif' },
];

export const ANIMATION_OPTIONS: { label: string; value: VideoAnimation; icon: string }[] = [
  { label: 'None', value: 'none', icon: 'ri-forbid-line' },
  { label: 'Fade In', value: 'fade-in', icon: 'ri-contrast-2-line' },
  { label: 'Slide Up', value: 'slide-up', icon: 'ri-arrow-up-line' },
  { label: 'Slide In Left', value: 'slide-left', icon: 'ri-arrow-left-line' },
  { label: 'Pop In', value: 'pop-in', icon: 'ri-zoom-in-line' },
];

export const DEFAULT_TEXT_OVERLAY: Omit<VideoOverlay, 'id'> = {
  type: 'text',
  content: 'Your Headline Here',
  x: 10,
  y: 70,
  width: 80,
  fontSize: 32,
  fontFamily: 'Montserrat, sans-serif',
  color: '#FFFFFF',
  backgroundColor: 'transparent',
  borderRadius: 0,
  fontWeight: 800,
  opacity: 1,
  animation: 'fade-in',
  startTime: 0.5,
  duration: 4,
  preset: 'headline',
};

export const DEFAULT_SUBHEADLINE_OVERLAY: Omit<VideoOverlay, 'id'> = {
  type: 'text',
  content: 'Supporting message goes here',
  x: 10,
  y: 82,
  width: 80,
  fontSize: 18,
  fontFamily: 'Inter, sans-serif',
  color: 'rgba(255,255,255,0.9)',
  backgroundColor: 'transparent',
  borderRadius: 0,
  fontWeight: 500,
  opacity: 1,
  animation: 'slide-up',
  startTime: 1,
  duration: 3.5,
  preset: 'subheadline',
};

export const DEFAULT_CTA_OVERLAY: Omit<VideoOverlay, 'id'> = {
  type: 'cta',
  content: 'Book Now',
  x: 10,
  y: 88,
  width: 40,
  fontSize: 14,
  fontFamily: 'Inter, sans-serif',
  color: '#FFFFFF',
  backgroundColor: '#f97316',
  borderRadius: 50,
  fontWeight: 700,
  opacity: 1,
  animation: 'pop-in',
  startTime: 1.5,
  duration: 3,
  icon: 'ri-arrow-right-line',
};

export const DEFAULT_LOGO_OVERLAY: Omit<VideoOverlay, 'id'> = {
  type: 'logo',
  content: '',
  x: 5,
  y: 5,
  width: 25,
  fontSize: 14,
  fontFamily: 'Inter, sans-serif',
  color: '#FFFFFF',
  backgroundColor: 'transparent',
  borderRadius: 8,
  fontWeight: 400,
  opacity: 1,
  animation: 'fade-in',
  startTime: 0,
  duration: 5,
};
